import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { canSeeFinancials } from '@/lib/constants';
import { ArrowLeft, RefreshCw, Save, ArrowRight, FileText } from 'lucide-react';
import { usePlyChangeHandler, PlyChangeDialog } from '@/components/DynamicFieldRenderer';
import { JobCardAttachments } from '@/components/attachments';
import SpecGrid from '@/components/SpecGrid';
import { Screen } from '@/components/data';
import ProductTypePicker from './JobCardFill/ProductTypePicker';
import ProcessPicker, { resolvePickedPipeline, instructionFieldNames } from './JobCardFill/ProcessPicker';

/**
 * Filling in a job card.
 *
 * This is the screen order entry used to open per line item, moved to where its
 * questions can actually be answered. It asks three things, left to right: what
 * is being made, how it is to be made, and everything else known about it. The
 * item does not move until this is saved — filling it in is what resolves the
 * pipeline, creates the first batch and puts the job on the floor.
 */

// managed from the Plate Tracking dashboard, never typed on a card
const PLATE_TRACKING_FIELDS = new Set(['plate_ordered', 'plate_ordered_at', 'plate_received', 'plate_received_at']);

function PaneHead({ title, aside }) {
  return (
    <h3 className="flex shrink-0 items-center justify-between rounded-t bg-brand-blue px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
      <span>{title}</span>
      {aside && <span className="text-[10px] font-normal normal-case tracking-normal text-blue-100">{aside}</span>}
    </h3>
  );
}

function Fact({ label, value }) {
  return (
    <span className="flex flex-col leading-tight">
      <span className="text-[9px] uppercase tracking-wider text-slate-400">{label}</span>
      <span className="text-xs font-medium text-slate-800">{value || '—'}</span>
    </span>
  );
}

export default function JobCardFillPage() {
  const { jobCardId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const showFinancials = canSeeFinancials(user);

  const [jobCard, setJobCard] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [orderType, setOrderType] = useState('');
  const [specs, setSpecs] = useState({});
  const [processOrder, setProcessOrder] = useState({});
  const [rulePreview, setRulePreview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [cardRes, configRes] = await Promise.all([
        api.get(`/job-cards/${jobCardId}`),
        api.get('/product-configs'),
      ]);
      const card = cardRes.data;
      setJobCard(card);
      setSpecs(card.specifications || {});
      setProcessOrder(card.process_order || {});
      setOrderType(card.product_type_code && card.product_type_code !== 'UNCLASSIFIED'
        ? card.product_type_code : '');
      setConfigs((configRes.data.configs || []).filter(c => !c.is_legacy));
    } catch {
      toast.error('Failed to load job card');
    }
    setLoading(false);
  }, [jobCardId]);

  useEffect(() => { load(); }, [load]);

  const config = useMemo(
    () => configs.find(c => c.code === orderType) || null,
    [configs, orderType],
  );

  const rules = useMemo(() => config?.stage_generation_rules || [], [config]);

  // Fields the process list owns. They are the pipeline decision written as a
  // spec, so showing them here as well asked the same question twice and let the
  // two answers disagree.
  const ownedByProcesses = useMemo(() => instructionFieldNames(rules), [rules]);

  const allFields = useMemo(
    () => (config?.job_card_fields || []).filter(f => !PLATE_TRACKING_FIELDS.has(f.name)),
    [config],
  );

  const fieldsByName = useMemo(
    () => Object.fromEntries(allFields.map(f => [f.name, f])),
    [allFields],
  );

  const fields = useMemo(
    () => allFields.filter(f => !ownedByProcesses.has(f.name)),
    [allFields, ownedByProcesses],
  );

  const fieldSections = useMemo(() => {
    const grouped = new Map();
    for (const field of [...fields].sort((a, b) => (a.display_order || 0) - (b.display_order || 0))) {
      const section = field.section || 'Specifications';
      if (!grouped.has(section)) grouped.set(section, []);
      grouped.get(section).push(field);
    }
    return [...grouped.entries()];
  }, [fields]);

  // Numbers picked here ARE the pipeline. Only when nothing is numbered do the
  // stage rules read the specs instead — the same fallback the server uses, so
  // the preview and the saved pipeline never disagree.
  const pickedPipeline = useMemo(
    () => resolvePickedPipeline(processOrder, rules),
    [processOrder, rules],
  );

  useEffect(() => {
    if (!orderType || Object.keys(processOrder).length > 0) { setRulePreview([]); return; }
    const timer = setTimeout(() => {
      api.post(`/job-cards/pipeline-preview/${orderType}`, { field_values: specs })
        .then(res => setRulePreview(res.data.stages || []))
        .catch(() => setRulePreview([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [orderType, specs, processOrder]);

  const pipeline = pickedPipeline.length > 0 ? pickedPipeline : rulePreview;

  const updateSpec = (name, value) => setSpecs(prev => ({ ...prev, [name]: value }));

  const isBlank = (value) =>
    value === undefined || value === null || value === '' ||
    (Array.isArray(value) && value.length === 0);

  const filledCount = fields.filter(f => !isBlank(specs[f.name])).length;
  const { plyDialog, handlePlyChange, confirmPlyChange, cancelPlyChange } = usePlyChangeHandler(specs, setSpecs);

  const pickType = (code) => {
    if (code === orderType) return;
    setOrderType(code);
    // the numbers named the old type's stages; they mean nothing to the new one
    setProcessOrder({});
  };

  const save = async () => {
    if (!orderType) { toast.error('Pick an order type first'); return; }
    setSaving(true);
    try {
      const res = await api.put(`/job-cards/${jobCard.id}/fill`, {
        order_type: orderType,
        specifications: specs,
        process_order: processOrder,
      });
      toast.success(res.data.production_started
        ? 'Job card saved — the item is on the floor'
        : 'Job card saved');
      navigate(`/job-card/${jobCard.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save job card');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-blue" />
      </div>
    );
  }

  if (!jobCard) {
    return (
      <div className="py-12 text-center text-slate-500">
        <FileText className="mx-auto mb-3 h-12 w-12 text-slate-300" />
        <p className="font-medium">Job card not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Go Back
        </Button>
      </div>
    );
  }

  const info = jobCard.job_info || {};
  const started = (jobCard.batches || []).some(b => (b.stage_history || []).length > 0);
  // Mirrors STAGE_EDIT_LOCK_AFTER in services/stage_engine.py: from two stages
  // on, the work already booked cannot be lined up against a rewritten list.
  const stagesPassed = Math.max(0, ...(jobCard.batches || []).map(b =>
    new Set((b.stage_history || []).filter(h => !h.reverted_at).map(h => h.stage_key)).size));
  const pipelineLocked = stagesPassed >= 2;
  const picked = Object.keys(processOrder).length;

  return (
    <Screen data-testid="job-card-fill-page">
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0">
            <h1 className="truncate font-heading text-lg font-bold text-slate-900">
              {jobCard.item_name || 'Job Card'}
            </h1>
            <p className="text-xs text-slate-500">
              {jobCard.order_display_id ? `Order ${jobCard.order_display_id} · ` : ''}
              {info.customer_name || 'Standalone'}
            </p>
          </div>
          {started && (
            <Badge className="border-amber-200 bg-amber-50 text-[10px] text-amber-700">Work started</Badge>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate(`/job-card/${jobCard.id}`)}>
            Process table <ArrowRight className="ml-1 h-3.5 w-3.5" />
          </Button>
          <Button onClick={save} disabled={saving || !orderType}
            className="bg-brand-blue px-6 text-white hover:bg-brand-blue-dark" data-testid="save-job-card-btn">
            {saving ? <RefreshCw className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
            {saving ? 'Saving…' : 'Save Job Card'}
          </Button>
        </div>
      </div>

      {/* The order line this card is for — read-only; it was settled when the
          order was booked and is not re-asked here. */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1.5 rounded border border-slate-200 bg-slate-50 px-3 py-1.5">
        <Fact label="Quantity" value={info.quantity?.toLocaleString()} />
        {showFinancials && <Fact label="Rate" value={info.item_value ? `₹${info.item_value.toLocaleString()}` : null} />}
        <Fact label="Delivery" value={info.expected_delivery_date?.slice(0, 10)} />
        <Fact label="Priority" value={info.priority} />
        <Fact label="Card filled" value={`${jobCard.completion_percentage || 0}%`} />
      </div>

      <div className="rounded border border-slate-200 bg-white px-3 py-1.5">
        <ProductTypePicker configs={configs} value={orderType} onSelect={pickType} locked={started} />
      </div>

      <div className="grid min-h-0 flex-1 gap-3 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_18rem]">
        {/* ── The product's own spec sheet ── */}
        <section className="flex min-h-0 flex-col">
          <PaneHead
            title="Technical Details"
            aside={config ? `${filledCount}/${fields.length} filled` : null}
          />
          <div className="min-h-0 flex-1 overflow-y-auto rounded-b border border-t-0 border-slate-200">
            {!config ? (
              <p className="px-3 py-6 text-center text-xs text-slate-400">
                Pick an order type to see its specification.
              </p>
            ) : fieldSections.map(([sectionName, sectionFields]) => (
              <React.Fragment key={sectionName}>
                <div className="bg-slate-200/70 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                  {sectionName}
                </div>
                <SpecGrid
                  fields={sectionFields}
                  values={specs}
                  onChange={updateSpec}
                  autoFilledFields={jobCard.auto_filled_fields || []}
                  onPlyChange={handlePlyChange}
                  columns={1}
                  orderType={orderType}
                />
              </React.Fragment>
            ))}
          </div>
        </section>

        {/* ── The pipeline ── */}
        <section className="flex min-h-0 flex-col">
          <PaneHead
            title="Production Pipeline"
            aside={picked ? `${picked} picked` : 'number them in running order'}
          />
          {/* the picker owns its own scroll: the search box above the list must
              not scroll away with it */}
          <div className="flex min-h-0 flex-1 flex-col border border-t-0 border-slate-200">
            {pipelineLocked ? (
              <div className="space-y-1.5 bg-slate-50 px-3 py-4" data-testid="pipeline-locked">
                <p className="text-xs font-semibold text-slate-700">Process list locked</p>
                <p className="text-[11px] leading-relaxed text-slate-500">
                  This job has already passed {stagesPassed} stages, so it keeps the process
                  list it ran on. Move it back to the stage the change starts from, or raise a
                  fresh job card for the work that is left. The specification can still be edited.
                </p>
              </div>
            ) : (
              <ProcessPicker
                rules={rules}
                processOrder={processOrder}
                onChange={setProcessOrder}
                hasType={!!config}
                fieldsByName={fieldsByName}
                specs={specs}
                onSpecChange={updateSpec}
              />
            )}
          </div>
          <div className="shrink-0 rounded-b border border-t-0 border-slate-200 bg-slate-50 px-2 py-1.5">
            {pipeline.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1" data-testid="pipeline-preview">
                {pipeline.map((stage, idx) => (
                  <span key={stage.stage_key}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white py-0.5 pl-1 pr-1.5 text-[10px] text-slate-700">
                    <span className="flex h-3 w-3 items-center justify-center rounded-full bg-brand-blue text-[8px] tabular-nums text-white">
                      {idx + 1}
                    </span>
                    {stage.stage_name}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-[10px] text-slate-400">
                Nothing numbered yet. Packing and Dispatch are added at the end automatically.
              </p>
            )}
          </div>
        </section>

        {/* ── Everything that travels with the item ── */}
        <section className="flex min-h-0 flex-col">
          <PaneHead title="Notes & Files" />
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto rounded-b border border-t-0 border-slate-200 p-2">
            <div className="space-y-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Notes</p>
              <Textarea
                value={specs.remarks || ''}
                onChange={e => updateSpec('remarks', e.target.value)}
                rows={3}
                className="resize-none text-xs"
                placeholder="Notes for the floor on this job…"
              />
            </div>
            {/* the pane already has a header; the attachments card must not
                bring a second one into the same column */}
            <JobCardAttachments jobCardId={jobCard.id} bare />
          </div>
        </section>
      </div>

      <PlyChangeDialog
        open={plyDialog.open}
        onConfirm={confirmPlyChange}
        onCancel={cancelPlyChange}
        oldPly={plyDialog.oldPly}
        newPly={plyDialog.newPly}
      />
    </Screen>
  );
}
